import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import {
    SEO_PRESENTATION,
    HON_PRESENTATION,
    ENGRAVE_PRESENTATION,
} from "@/content/seoHonEngravePresentation";

export interface WeeklyDigestPayload {
    weekNo: number;
    actionLabel: string;
    summaryTone: "steady" | "warning" | "danger" | "good";
    summaryTitle: string;
    summaryBody: string;
    immediateFocus: string;
    immediateFocusDetail: string;
    warnings: string[];
    statDelta: Record<string, number>;
    resourceDelta: Record<string, number>;
    storyHighlights: string[];
    affinityHighlights: string[];
    nextHintTitle: string;
    nextHintBody: string;
    trajectoryTitle?: string;
    trajectoryBody?: string;
    trajectoryTone?: "steady" | "warning" | "danger" | "good";
}

interface WeeklyDigestModalProps {
    open: boolean;
    digest: WeeklyDigestPayload | null;
    onClose: () => void;
}

export function WeeklyDigestModal({ open, digest, onClose }: WeeklyDigestModalProps) {
    if (!open || !digest) return null;

    const statEntries = Object.entries(digest.statDelta ?? {}).filter(([, value]) => Boolean(value));
    const resourceEntries = Object.entries(digest.resourceDelta ?? {}).filter(([, value]) => Boolean(value));
    const featuredDelta = [...statEntries.map(([key, value]) => ({
        key: `stat-${key}`,
        label: statLabel(key),
        value,
        accent: value > 0 ? tokens.color.moss : tokens.color.burgundy,
        kindLabel: "이번 주 대표 변화",
    })), ...resourceEntries.map(([key, value]) => ({
        key: `resource-${key}`,
        label: resourceLabel(key),
        value,
        accent: value > 0 ? tokens.color.brass : tokens.color.ember,
        kindLabel: "즉시 흔들린 축",
    }))].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0] ?? null;
    const summaryAccent = digest.summaryTone === "danger"
        ? tokens.color.burgundy
        : digest.summaryTone === "warning"
            ? tokens.color.ember
            : digest.summaryTone === "good"
                ? tokens.color.moss
                : tokens.color.brass;
    const trajectoryAccent = digest.trajectoryTone === "danger"
        ? tokens.color.burgundy
        : digest.trajectoryTone === "warning"
            ? tokens.color.ember
            : digest.trajectoryTone === "good"
                ? tokens.color.moss
                : tokens.color.steel;
    const lexiconBridge = weeklyDigestLexiconBridge(digest.actionLabel);

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{
                zIndex: tokens.elevation.modal + 1,
                background: "radial-gradient(circle at 50% 36%, rgba(92,31,46,0.20) 0%, rgba(11,9,18,0.84) 44%, rgba(11,9,18,0.92) 100%)",
                backdropFilter: "blur(6px)",
            }}
        >
            {/* 2026-04-24: maxHeight 90vh + overflowY auto 추가.
                이전엔 컨텐츠가 길어지면 모달이 뷰포트 아래로 삐져나가 "결과 확인 버튼 찾으러
                내려가야 하는" 문제. 이제 모달 내부에서만 스크롤되고 footer 버튼은 컨텐츠
                끝자락에 자연스럽게 노출. 배경 illustrations 는 absolute inset-0 이라 스크롤
                영향 없이 함께 움직인다. */}
            <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.26, ease: tokens.easing.decelerate }}
                className="relative w-full max-w-[980px] rounded-l p-xl"
                style={{
                    background: `linear-gradient(180deg, ${tokens.color.ink}F2, ${tokens.color.ink}E8)`,
                    border: `1px solid ${tokens.color.brass}44`,
                    boxShadow: `0 0 44px ${tokens.color.ember}20`,
                    maxHeight: "90vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                <img
                    src="/art/illustrations/weekly-turn-chamber-aurora.png"
                    alt=""
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                    style={{
                        objectFit: "cover",
                        objectPosition: "center 28%",
                        opacity: 0.22,
                        mixBlendMode: "screen",
                    }}
                />
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background: "linear-gradient(180deg, rgba(10,8,16,0.12), rgba(10,8,16,0.34) 46%, rgba(10,8,16,0.58))",
                    }}
                />
                <div
                    className="relative mb-l rounded-m p-m"
                    style={{
                        background: `linear-gradient(135deg, ${summaryAccent}18, ${tokens.color.ink}88 62%)`,
                        border: `1px solid ${summaryAccent}44`,
                        boxShadow: `0 0 24px ${summaryAccent}18`,
                    }}
                >
                    <div className="flex flex-wrap items-start justify-between gap-m">
                        <div className="min-w-0 flex-1">
                            <div
                                className="font-display"
                                style={{
                                    color: summaryAccent,
                                    fontSize: tokens.type.size.tiny,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                }}
                            >
                                이번 주의 성격
                            </div>
                            <div
                                className="mt-[4px] font-display"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.subtitle,
                                    letterSpacing: tokens.type.letterSpacing.normal,
                                }}
                            >
                                {digest.summaryTitle}
                            </div>
                            <div
                                className="mt-[6px] font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.body,
                                    lineHeight: tokens.type.lineHeight.normal,
                                }}
                            >
                                {digest.summaryBody}
                            </div>
                        </div>
                        <div className="grid min-w-[280px] gap-s">
                            {digest.trajectoryTitle && digest.trajectoryBody && (
                                <DigestCallout
                                    label="이번 주 결말 흐름"
                                    value={digest.trajectoryTitle}
                                    body={digest.trajectoryBody}
                                    accent={trajectoryAccent}
                                />
                            )}
                            <DigestCallout
                                label="가장 크게 남은 것"
                                value={digest.immediateFocus}
                                body={digest.immediateFocusDetail}
                                accent={summaryAccent}
                            />
                            <DigestCallout
                                label="다음 주 경고"
                                value={digest.nextHintTitle}
                                body={digest.nextHintBody}
                                accent={tokens.color.brass}
                            />
                        </div>
                    </div>
                    {digest.warnings.length > 0 && (
                        <div className="mt-s flex flex-wrap gap-xs">
                            {digest.warnings.map((warning) => (
                                <div
                                    key={warning}
                                    className="rounded-pill px-s py-[4px] font-display"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.tiny,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        background: `${tokens.color.ink}A8`,
                                        border: `1px solid ${summaryAccent}33`,
                                    }}
                                >
                                    {warning}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="relative flex items-start justify-between gap-l">
                    <div>
                        {/* 2026-04-25: "제N주 결산 장부" kicker + "방금 지나간 한 주를..." 설명문 제거.
                            타이틀 "제N주 · 행동" 이 이미 의미 전달. */}
                        <div
                            className="font-display"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: "clamp(22px, 1.8vw, 30px)",
                                letterSpacing: "0.04em",
                                textShadow: `0 0 18px ${tokens.color.ember}44, 0 2px 0 ${tokens.color.ink}`,
                            }}
                        >
                            제{digest.weekNo}주 · {digest.actionLabel}
                        </div>
                        {lexiconBridge && (
                            <div
                                className="mt-m rounded-m px-m py-s"
                                style={{
                                    background: `${lexiconBridge.accent}12`,
                                    border: `1px solid ${lexiconBridge.accent}33`,
                                    boxShadow: `0 0 18px ${lexiconBridge.accent}14`,
                                }}
                            >
                                <div
                                    className="font-display"
                                    style={{
                                        color: lexiconBridge.accent,
                                        fontSize: tokens.type.size.small,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    {lexiconBridge.title}
                                </div>
                                <div
                                    className="mt-[3px] font-body"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.small,
                                        lineHeight: tokens.type.lineHeight.normal,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    {lexiconBridge.body}
                                </div>
                                <div className="mt-xs flex flex-wrap gap-[4px]">
                                    {lexiconBridge.entries.map((entry) => (
                                        <span
                                            key={entry}
                                            className="rounded-pill px-s py-[2px] font-body whitespace-nowrap"
                                            style={{
                                                color: tokens.color.parchment,
                                                background: `${tokens.color.ink}AA`,
                                                border: `1px solid ${lexiconBridge.accent}33`,
                                                fontSize: 13.5,
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            {entry}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {featuredDelta && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.08, duration: 0.24, ease: "easeOut" }}
                                className="mt-m inline-flex items-center gap-m rounded-l px-m py-s"
                                style={{
                                    background: `linear-gradient(135deg, ${featuredDelta.accent}18, ${tokens.color.ink}A0 64%)`,
                                    border: `1px solid ${featuredDelta.accent}44`,
                                    boxShadow: `0 0 18px ${featuredDelta.accent}18`,
                                }}
                            >
                                <div>
                                    <div
                                        className="font-display"
                                        style={{
                                            color: featuredDelta.accent,
                                            fontSize: tokens.type.size.tiny,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                        }}
                                    >
                                        {featuredDelta.kindLabel}
                                    </div>
                                    <div
                                        className="mt-[2px] font-display"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: tokens.type.size.small,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                        }}
                                    >
                                        {featuredDelta.label}
                                    </div>
                                </div>
                                <div
                                    className="font-stat"
                                    style={{
                                        color: featuredDelta.accent,
                                        fontSize: tokens.type.size.subtitle,
                                        textShadow: `0 0 14px ${featuredDelta.accent}55`,
                                    }}
                                >
                                    {signed(featuredDelta.value)}
                                </div>
                            </motion.div>
                        )}
                    </div>
                    <div
                        className="rounded-pill px-s py-[4px] font-display"
                        style={{
                            color: tokens.color.ember,
                            background: `${tokens.color.ember}12`,
                            border: `1px solid ${tokens.color.ember}33`,
                            fontSize: 13.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        주차 보고
                    </div>
                </div>

                <div className="relative mt-l grid gap-m lg:grid-cols-[1.15fr_0.95fr]">
                    <div className="grid gap-m">
                        <DigestSection
                            title="이번 주 성장"
                            subtitle="숫자로 남은 변화들"
                            accent={tokens.color.ember}
                        >
                            <div className="grid gap-s md:grid-cols-2">
                                {statEntries.length > 0 ? statEntries.map(([key, value], index) => (
                                    <DeltaCard
                                        key={`stat-${key}`}
                                        index={index}
                                        title={statLabel(key)}
                                        value={signed(value)}
                                        accent={value > 0 ? tokens.color.moss : tokens.color.burgundy}
                                        detail={value > 0 ? "이번 주에 분명히 자랐습니다." : "이번 주의 여파가 남았습니다."}
                                    />
                                )) : (
                                    <EmptyDeltaCard text="이번 주에는 직접 오른 스탯이 없었습니다." />
                                )}
                            </div>
                        </DigestSection>

                        <DigestSection
                            title="이번 주 이야기"
                            subtitle="이번 순에 실제로 지나간 장면들"
                            accent={tokens.color.burgundy}
                        >
                            <div className="grid gap-s">
                                {digest.storyHighlights.length > 0 ? digest.storyHighlights.map((line, index) => (
                                    <motion.div
                                        key={`${line}-${index}`}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.08 * index, duration: 0.24, ease: "easeOut" }}
                                        className="rounded-m px-m py-s"
                                        style={{
                                            background: `${tokens.color.ink}88`,
                                            border: `1px solid ${tokens.color.parchmentDim}22`,
                                        }}
                                    >
                                        <div
                                            className="font-body"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: tokens.type.size.small,
                                                lineHeight: tokens.type.lineHeight.normal,
                                            }}
                                        >
                                            {line}
                                        </div>
                                    </motion.div>
                                )) : (
                                    <EmptyDeltaCard text="이번 주는 큰 고정 사건 없이 지나갔습니다." />
                                )}
                            </div>
                        </DigestSection>
                    </div>

                    <div className="grid gap-m">
                        <DigestSection
                            title="바로 흔들린 것"
                            subtitle="자원과 원정 상태 변화"
                            accent={tokens.color.steel}
                        >
                            <div className="grid gap-s">
                                {resourceEntries.length > 0 ? resourceEntries.map(([key, value], index) => (
                                    <DeltaCard
                                        key={`resource-${key}`}
                                        index={index}
                                        title={resourceLabel(key)}
                                        value={signed(value)}
                                        accent={value > 0 ? tokens.color.moss : tokens.color.ember}
                                        detail={value > 0 ? "이번 주엔 여유가 조금 생겼습니다." : "이번 주엔 이만큼 깎여 나갔습니다."}
                                    />
                                )) : (
                                    <EmptyDeltaCard text="자원은 큰 변화 없이 유지됐습니다." />
                                )}
                            </div>
                        </DigestSection>

                        <DigestSection
                            title="관계와 여운"
                            subtitle="이번 주 함께 움직인 운명들"
                            accent={tokens.color.moss}
                        >
                            <div className="grid gap-s">
                                {digest.affinityHighlights.length > 0 ? digest.affinityHighlights.map((line, index) => (
                                    <motion.div
                                        key={`${line}-${index}`}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.07 * index, duration: 0.24, ease: "easeOut" }}
                                        className="rounded-m px-m py-s"
                                        style={{
                                            background: `${tokens.color.moss}10`,
                                            border: `1px solid ${tokens.color.moss}24`,
                                        }}
                                    >
                                        <div
                                            className="font-body"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: tokens.type.size.small,
                                                lineHeight: tokens.type.lineHeight.normal,
                                            }}
                                        >
                                            {line}
                                        </div>
                                    </motion.div>
                                )) : (
                                    <EmptyDeltaCard text="이번 주엔 특별히 가까워진 인연이 드러나지 않았습니다." />
                                )}
                            </div>
                        </DigestSection>

                        <DigestSection
                            title="다음에 준비할 것"
                            subtitle={digest.nextHintTitle}
                            accent={tokens.color.brass}
                        >
                            <div
                                className="rounded-m px-m py-m"
                                style={{
                                    background: `${tokens.color.brass}10`,
                                    border: `1px solid ${tokens.color.brass}24`,
                                }}
                            >
                                <div
                                    className="font-body"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.small,
                                        lineHeight: tokens.type.lineHeight.normal,
                                    }}
                                >
                                    {digest.nextHintBody}
                                </div>
                            </div>
                        </DigestSection>
                    </div>
                </div>

                {/* 2026-04-23: 하단 footer — 금속 hair-line + breathing glow 버튼. */}
                <div
                    aria-hidden
                    className="relative mt-l"
                    style={{
                        height: 1,
                        background: `linear-gradient(90deg, transparent 0%, ${tokens.color.brass}66 30%, ${tokens.color.brass}66 70%, transparent 100%)`,
                    }}
                />
                <div className="relative mt-m flex justify-end">
                    <motion.div
                        animate={{
                            boxShadow: [
                                `0 0 16px ${tokens.color.ember}22`,
                                `0 0 32px ${tokens.color.ember}44`,
                                `0 0 16px ${tokens.color.ember}22`,
                            ],
                        }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="rounded-m"
                    >
                        <PrimaryButton onClick={onClose} size="m" soundOnClick="confirm">
                            다음 주로 넘어간다
                        </PrimaryButton>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
}

function weeklyDigestLexiconBridge(actionLabel: string): {
    title: string;
    body: string;
    accent: string;
    entries: string[];
} | null {
    if (actionLabel.includes("훈련")) {
        return {
            title: "이번 주 훈련이 닿는 서",
            body: "몸을 밀어 붙인 주간일수록 전투의 글이 또렷해집니다. 이런 주차가 쌓이면 특정 서의 감각이 분명하게 손에 붙습니다.",
            accent: tokens.color.ember,
            entries: Object.values(SEO_PRESENTATION).slice(0, 4).map((entry) => entry.title),
        };
    }

    if (actionLabel.includes("연구")) {
        return {
            title: "이번 주 연구가 밝히는 혼",
            body: "책상과 의식에 오래 붙어 있던 주간은 혼의 결을 선명하게 드러냅니다. 마력 계열 빌드는 이런 축적에서 차이가 벌어집니다.",
            accent: tokens.color.brass,
            entries: Object.values(HON_PRESENTATION).slice(0, 4).map((entry) => entry.title),
        };
    }

    if (actionLabel.includes("제작") || actionLabel.includes("연성")) {
        return {
            title: "이번 주 공방이 남기는 각인",
            body: "재료와 손끝이 맞닿는 주간은 장비에 짧은 이름을 남깁니다. 좋은 연성은 긴 설명보다 먼저 각인의 말을 낳습니다.",
            accent: tokens.color.moss,
            entries: Object.values(ENGRAVE_PRESENTATION).slice(0, 5).map((entry) => entry.title),
        };
    }

    return null;
}

function DigestSection({
    title,
    accent,
    children,
}: {
    title: string;
    /** 2026-04-25: subtitle 받지만 더 이상 렌더링 안 함 — 호출부 그대로 유지하면서 화면만 깔끔. */
    subtitle?: string;
    accent: string;
    children: React.ReactNode;
}) {
    return (
        <div
            className="rounded-m p-m"
            style={{
                background: `linear-gradient(180deg, ${tokens.color.parchment}0B 0%, ${tokens.color.parchment}04 100%)`,
                border: `1px solid ${accent}2A`,
                boxShadow: `inset 0 1px 0 ${accent}1A`,
            }}
        >
            <div
                className="font-display flex items-center gap-xs mb-s"
                style={{
                    color: accent,
                    fontSize: 13.5,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 8,
                        height: 1,
                        background: accent,
                        boxShadow: `0 0 6px ${accent}AA`,
                        opacity: 0.7,
                    }}
                />
                {title}
            </div>
            {children}
        </div>
    );
}

function DeltaCard({
    title,
    value,
    accent,
    detail,
    index,
}: {
    title: string;
    value: string;
    accent: string;
    detail: string;
    index: number;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * index, duration: 0.22, ease: "easeOut" }}
            className="rounded-m px-m py-s"
            style={{
                background: `linear-gradient(180deg, ${accent}10, ${tokens.color.ink}78)`,
                border: `1px solid ${accent}33`,
            }}
        >
            <div className="flex items-baseline justify-between gap-s">
                <div
                    className="font-display"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        letterSpacing: tokens.type.letterSpacing.wide,
                    }}
                >
                    {title}
                </div>
                <div
                    className="font-stat"
                    style={{
                        color: accent,
                        fontSize: tokens.type.size.small,
                    }}
                >
                    {value}
                </div>
            </div>
            <div
                className="mt-[3px] font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 14,
                    lineHeight: tokens.type.lineHeight.normal,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {detail}
            </div>
        </motion.div>
    );
}

function EmptyDeltaCard({ text }: { text: string }) {
    return (
        <div
            className="rounded-m px-m py-s"
            style={{
                background: `${tokens.color.ink}72`,
                border: `1px dashed ${tokens.color.parchmentDim}24`,
            }}
        >
            <div
                className="font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 14.5,
                    lineHeight: tokens.type.lineHeight.normal,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {text}
            </div>
        </div>
    );
}

function DigestCallout({
    label,
    value,
    body,
    accent,
}: {
    label: string;
    value: string;
    body: string;
    accent: string;
}) {
    return (
        <div
            className="rounded-m px-m py-s"
            style={{
                background: `${accent}10`,
                border: `1px solid ${accent}28`,
            }}
        >
            <div
                className="font-display"
                style={{
                    color: accent,
                    fontSize: tokens.type.size.small,
                    letterSpacing: tokens.type.letterSpacing.wide,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {label}
            </div>
            <div
                className="mt-[4px] font-display"
                style={{
                    color: tokens.color.parchment,
                    fontSize: tokens.type.size.small,
                    letterSpacing: tokens.type.letterSpacing.normal,
                }}
            >
                {value}
            </div>
            <div
                className="mt-[4px] font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: tokens.type.size.small,
                    lineHeight: tokens.type.lineHeight.normal,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {body}
            </div>
        </div>
    );
}

function statLabel(code: string): string {
    switch (code) {
        case "str": return "근력";
        case "vit": return "활력";
        case "tec": return "기술";
        case "int": return "지력";
        case "spi": return "정신";
        case "cha": return "매력";
        case "spd": return "속도";
        default: return code.toUpperCase();
    }
}

function resourceLabel(code: string): string {
    switch (code) {
        case "stamina": return "체력";
        case "mana": return "정신";
        case "gold": return "자금";
        default: return code;
    }
}

function signed(value: number): string {
    return value > 0 ? `+${value}` : `${value}`;
}
